// SHT RACI Matrix Data - COMPREHENSIVE UI ANALYSIS v5.0
// All permissions verified against QA branch code including department-based access control
// and MLR approval workflow with combined reviewer/approver selection at submission
// Date: February 20, 2026

export const raciMatrixData = {
  features: [
    // === SPECIALTY LIST MANAGEMENT ===
    {
      id: "create_lists",
      name: "Create New Specialty Lists",
      description: "Create new specialty lists from scratch or from existing templates. Restrictions: HCP SF Marketer can only create from existing (no scratch), Incentivized Approver cannot create any lists, other restricted roles per canCreateList(). Department restrictions: HCP Marketer can ONLY create Commercial (RETAIL) lists, Omnichannel Advisor can ONLY create Medical (MEDICAL) lists.",
      category: "Specialty List Management",
      codeReference: "Overview.js canCreateList(), CreateNewListModal.js checkSFMarketerRole(), CreateListModal.js getDepartmentOptions()",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true,
        spclty_hub_viewer: false
      }
    },
    {
      id: "edit_list_title",
      name: "Edit List Title",
      description: "Edit the title of specialty lists. Super Admin can edit all lists. Other roles (Admin, HCP Marketer, HCP SF Marketer, BIA Team, Omnichannel Advisor) can edit non-approved lists only. Cannot edit archived or approved lists (except Super Admin). Department restrictions: HCP Marketer can ONLY edit Commercial lists, Omnichannel Advisor can ONLY edit Medical lists (view-only for other departments). Creator-only for DRAFT lists: Only the list creator or admin can edit DRAFT list titles.",
      category: "Specialty List Management",
      codeReference: "OverviewTable.js allowToEditTitleAndMetaData() lines 1378-1437, hasDepartmentAccess() lines 133-185, isCreatorOrAdmin() lines 108-130",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for Commercial lists they created (DRAFT) or own department
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for Medical lists they created (DRAFT) or own department
        spclty_hub_viewer: false
      }
    },
    {
      id: "edit_metadata",
      name: "Edit List Metadata",
      description: "Edit specialty list metadata, descriptions, and properties (excluding title). Super Admin can edit all lists. Other roles (Admin, HCP Marketer, HCP SF Marketer, BIA Team, Omnichannel Advisor) can edit non-approved lists only. Cannot edit archived or approved lists (except Super Admin). Department restrictions: HCP Marketer can ONLY edit/set to Commercial (RETAIL) on their department lists, Omnichannel Advisor can ONLY edit/set to Medical (MEDICAL) on their department lists. Users can VIEW all lists but edit buttons hidden for wrong department. Creator-only for DRAFT lists: Only the list creator or admin can edit DRAFT list metadata.",
      category: "Specialty List Management",
      codeReference: "OverviewTable.js allowToEditTitleAndMetaData() lines 1378-1437, hasDepartmentAccess() lines 133-185, EditBrandIndication.js getDepartmentOptions() lines 395-437, Details.js view-only access lines 163-167",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for Commercial lists, can VIEW Medical lists
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for Medical lists, can VIEW Commercial lists
        spclty_hub_viewer: false
      }
    },
    {
      id: "delete_lists",
      name: "Delete Draft Lists",
      description: "Delete draft specialty lists that have not been approved. Roles that can create lists can also delete draft lists. Only draft or non-approved lists can be deleted. Cannot delete approved lists - use archive instead.",
      category: "Specialty List Management",
      codeReference: "OverviewTile.js canDeleteDrafts() lines 71-80",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true,
        spclty_hub_viewer: false
      }
    },
    {
      id: "archive_lists",
      name: "Archive Approved Specialty Lists",
      description: "Archive approved specialty lists that are no longer active or have been superseded. This function is for approved lists only - draft lists should be deleted instead. Available to Super Admin and Admin roles to maintain data integrity and audit controls.",
      category: "Specialty List Management",
      codeReference: "OverviewTile.js archive functionality, canArchiveLists() check",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "import_data",
      name: "Import Specialty Data",
      description: "Import specialty data from external sources. Super Admin exclusive feature accessible through /superadmin/import route.",
      category: "Specialty List Management",
      codeReference: "middleware.js lines 55-59, SideNavBar.js SA-only Import menu",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: false,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },

    // === APPROVAL WORKFLOWS ===
    {
      id: "approve_mlr_list",
      name: "Approve MLR Lists",
      description: "Final approval authority for MLR (Medical Legal Review) specialty lists. MLR Approver and Super Admin can execute final MLR approval when list status is 'REQUEST FOR APPROVAL'.",
      category: "Approval Workflows",
      codeReference: "OverviewTable.js lines 795-827 MLR approval logic, ListApprovedModal.js",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: false,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: true,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "review_mlr_list",
      name: "Review MLR Lists",
      description: "Review and add comments to MLR specialty lists before final approval. MLR Reviewer and Super Admin can examine lists, add review comments, request changes, and validate MLR compliance when list status is 'REQUEST FOR REVIEW'. After all reviewers submit, list automatically transitions to 'REQUEST FOR APPROVAL' status (approvers were already selected at submission time).",
      category: "Approval Workflows",
      codeReference: "OverviewTable.js lines 1077-1090 (PENDING status transition), confirmSubmission() lines 828-834, updateSpecialityDetails() lines 1087-1089",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: false,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: true,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "approve_salesforce_list",
      name: "Approve Salesforce Lists",
      description: "Approval authority for Salesforce specialty lists. Incentivized Approver and Super Admin can approve Salesforce lists when status is 'REQUEST FOR APPROVAL'.",
      category: "Approval Workflows",
      codeReference: "OverviewTable.js lines 845-868 Salesforce approval logic, ListApprovedModal.js checkIncenApproverRole()",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: false,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: true,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "select_reviewers_approvers",
      name: "Select Reviewers & Approvers at Submission",
      description: "When submitting an MLR list from DRAFT, the creator must select BOTH reviewers AND approvers at the same time. After all reviewers complete their reviews, the list automatically transitions to REQUEST FOR APPROVAL. Approvers can only approve after all reviews are complete. This eliminates the intermediate PENDING ROUTING FOR APPROVAL step.",
      category: "Approval Workflows",
      codeReference: "ListApprovedModal.js (combined reviewer+approver selection), OverviewTable.js updateSpecialityDetails() (stores both lists), confirmSubmission()",
      permissions: {
        spclty_hub_super_admin: true, // Can submit any list
        spclty_hub_admin: true, // Can submit any list
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for lists they created
        spclty_hub_hcp_sf_marketer: false, // SF lists don't use MLR workflow
        spclty_hub_mlr_reviewer: false, // Cannot submit lists
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for lists they created
        spclty_hub_viewer: false
      }
    },
    {
      id: "route_draft_for_review",
      name: "Route DRAFT Lists for Review",
      description: "NEW RESTRICTION: Submit DRAFT lists for review (DRAFT → REQUEST FOR REVIEW). Only the list CREATOR or Admin/Super Admin can route their own DRAFT lists for review. Other users with the same role cannot route DRAFT lists created by someone else. This ensures list ownership and accountability.",
      category: "Approval Workflows",
      codeReference: "OverviewTable.js confirmSubmission() lines 913-920, checkdisabled() lines 1460-1467, showSubmitDraftButtons() lines 378-395, isCreatorOrAdmin() lines 108-130, CreateListModal.js currentRoleUuid lines 44-65",
      permissions: {
        spclty_hub_super_admin: true, // Can route any DRAFT list
        spclty_hub_admin: true, // Can route any DRAFT list
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for DRAFT lists they created
        spclty_hub_hcp_sf_marketer: true, // Only for DRAFT lists they created
        spclty_hub_mlr_reviewer: false, // Cannot create or route DRAFT lists
        spclty_hub_mlr_approver: false, // Cannot create or route DRAFT lists
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for DRAFT lists they created
        spclty_hub_viewer: false
      }
    },
    {
      id: "comment_system",
      name: "Add Comments",
      description: "Add comments and feedback on specialty lists. All roles except Viewer can add comments to non-archived lists. MLR Reviewer can add review-specific comments during the review process.",
      category: "Approval Workflows",
      codeReference: "Comments.js checkListStatus() function, comment permissions",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: true,
        spclty_hub_mlr_approver: true,
        spclty_hub_incentivized_approver: true,
        spclty_hub_omnichannel_advisor: true,
        spclty_hub_viewer: false
      }
    },

    // === DATA MANAGEMENT ===
    {
      id: "export_all_lists",
      name: "Export All Specialty Lists",
      description: "Export all specialty lists from the Overview page. Downloads comprehensive data of all lists in CSV format. Available to Admin, Super Admin, and BIA Team for bulk export functionality.",
      category: "Data Management",
      codeReference: "Overview.js canExportAllLists() function, FETCH_EXPORT_SPECIALTY_LIST action",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: true,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "export_individual_list",
      name: "Export Individual List Data",
      description: "Export data for a specific specialty list from the Details page. Downloads individual list details including specialties, codes, and metadata. Available to all roles except Viewer.",
      category: "Data Management",
      codeReference: "OverviewTable.js lines 1208-1213, CheckViewerRole() function, downloadcsv() lines 125+",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: true,
        spclty_hub_hcp_marketer: true,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: true,
        spclty_hub_mlr_approver: true,
        spclty_hub_incentivized_approver: true,
        spclty_hub_omnichannel_advisor: true,
        spclty_hub_viewer: false
      }
    },
    {
      id: "view_details",
      name: "View List Details",
      description: "Access detailed view of specialty lists including full metadata, history, and related data. BIA team now has access to view list details. All users can VIEW lists from any department (Medical, Commercial, Salesforce), but edit buttons are hidden based on department access. HCP Marketer can view Medical lists (read-only), Omnichannel Advisor can view Commercial lists (read-only).",
      category: "Data Management",
      codeReference: "middleware.js (BIA /details/ restriction removed), Details.js lines 163-167 (view-only access), OverviewTable.js hasDepartmentAccess() lines 133-185",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: true,
        spclty_hub_hcp_marketer: true,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: true,
        spclty_hub_mlr_approver: true,
        spclty_hub_incentivized_approver: true,
        spclty_hub_omnichannel_advisor: true,
        spclty_hub_viewer: true
      }
    },

    // === SPECIALTY & RC MANAGEMENT ===
    {
      id: "add_specialty_codes",
      name: "Add Specialty Codes to Lists",
      description: "Add specialty codes to specialty lists. Available to list creators and editors when list is not approved or archived. Blocked for approver roles and viewers. Department restrictions: HCP Marketer can ONLY add to Commercial lists, Omnichannel Advisor can ONLY add to Medical lists. HCP SF Marketer does NOT have access - they manage RC codes instead. Creator-only for DRAFT lists: Only the list creator or admin can add specialty codes to DRAFT lists.",
      category: "Specialty & RC Management",
      codeReference: "OverviewTable.js checkListStatus() lines 326-362, hasDepartmentAccess() lines 133-185, isCreatorOrAdmin() lines 108-130, Details.js checkRoleSFMarketer() lines 77-104",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for Commercial lists they can edit
        spclty_hub_hcp_sf_marketer: false, // SF Marketer manages RC codes, not specialty codes
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for Medical lists they can edit
        spclty_hub_viewer: false
      }
    },
    {
      id: "remove_specialty_codes",
      name: "Remove Specialty Codes from Lists",
      description: "Remove specialty codes from specialty lists. Available to list creators and editors when list is not approved or archived. Blocked for approver roles and viewers. Department restrictions: HCP Marketer can ONLY remove from Commercial lists, Omnichannel Advisor can ONLY remove from Medical lists. HCP SF Marketer does NOT have access - they manage RC codes instead. Creator-only for DRAFT lists: Only the list creator or admin can remove specialty codes from DRAFT lists.",
      category: "Specialty & RC Management",
      codeReference: "OverviewTable.js showDeleteButton() lines 614-661, hasDepartmentAccess() lines 133-185, isCreatorOrAdmin() lines 108-130",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: true, // Only for Commercial lists they can edit
        spclty_hub_hcp_sf_marketer: false, // SF Marketer manages RC codes, not specialty codes
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: true, // Only for Medical lists they can edit
        spclty_hub_viewer: false
      }
    },
    {
      id: "add_responsibility_codes",
      name: "Add Responsibility Codes to Specialties",
      description: "Add and assign responsibility codes to individual specialties within lists. Only for Salesforce (incentivized) lists. Available to Admin/Super Admin and HCP SF Marketer when list is not approved or archived. HCP Marketer and Omnichannel Advisor do not have access as they only create MLR lists which don't have RC codes.",
      category: "Specialty & RC Management",
      codeReference: "OverviewTable.js lines 594-605, 246-260 responsibility code controls",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "remove_responsibility_codes",
      name: "Remove Responsibility Codes from Specialties",
      description: "Remove responsibility codes from individual specialties within lists. Only for Salesforce (incentivized) lists. Available to Admin/Super Admin and HCP SF Marketer when list is not approved or archived. HCP Marketer and Omnichannel Advisor do not have access as they only create MLR lists which don't have RC codes.",
      category: "Specialty & RC Management",
      codeReference: "OverviewTable.js lines 594-605, 246-260 responsibility code controls",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: true,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },

    // === ADVANCED FEATURES ===
    {
      id: "user_administration",
      name: "User Administration",
      description: "Manage users, roles, and permissions. Access admin routes for user management. Blocked by middleware for non-admin roles.",
      category: "Advanced Features",
      codeReference: "middleware.js lines 47-52 admin route restrictions, Users.js admin features",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: true,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    },
    {
      id: "system_import",
      name: "System Data Import",
      description: "Import system-level data and configurations. Super Admin exclusive access to system import functionality through dedicated routes.",
      category: "Advanced Features",
      codeReference: "middleware.js lines 55-59 SA import restrictions, SideNavBar.js SA Import menu",
      permissions: {
        spclty_hub_super_admin: true,
        spclty_hub_admin: false,
        spclty_hub_bia_team: false,
        spclty_hub_hcp_marketer: false,
        spclty_hub_hcp_sf_marketer: false,
        spclty_hub_mlr_reviewer: false,
        spclty_hub_mlr_approver: false,
        spclty_hub_incentivized_approver: false,
        spclty_hub_omnichannel_advisor: false,
        spclty_hub_viewer: false
      }
    }
  ],

  roles: [
    { id: 'spclty_hub_super_admin', name: 'Super Admin', code: 'spclty_hub_super_admin' },
    { id: 'spclty_hub_admin', name: 'Admin', code: 'spclty_hub_admin' },
    { id: 'spclty_hub_bia_team', name: 'BIA Team', code: 'spclty_hub_bia_team' },
    { id: 'spclty_hub_hcp_marketer', name: 'HCP Marketer', code: 'spclty_hub_hcp_marketer' },
    { id: 'spclty_hub_hcp_sf_marketer', name: 'HCP SF Marketer', code: 'spclty_hub_hcp_sf_marketer' },
    { id: 'spclty_hub_mlr_reviewer', name: 'MLR Reviewer', code: 'spclty_hub_mlr_reviewer' },
    { id: 'spclty_hub_mlr_approver', name: 'MLR Approver', code: 'spclty_hub_mlr_approver' },
    { id: 'spclty_hub_incentivized_approver', name: 'Incentivized Approver', code: 'spclty_hub_incentivized_approver' },
    { id: 'spclty_hub_omnichannel_advisor', name: 'Omnichannel Advisor', code: 'spclty_hub_omnichannel_advisor' },
    { id: 'spclty_hub_viewer', name: 'Viewer', code: 'spclty_hub_viewer' }
  ],

  categories: [
    'Specialty List Management',
    'Approval Workflows',
    'Data Management',
    'Specialty & RC Management',
    'Advanced Features'
  ],

  metadata: {
    version: "5.0",
    analysisDate: "2026-02-20",
    totalFeatures: 21,
    totalRoles: 10,
    verificationLevel: "100%",
    codebaseAnalyzed: "QA Branch - 234+ frontend files + Lambda functions",
    lastUpdate: "MAJOR UPDATE: Combined reviewer/approver selection at submission time, removed PENDING ROUTING FOR APPROVAL status, auto-transition to REQUEST FOR APPROVAL after all reviews complete",
    keyChanges: [
      "Department Access: Users can VIEW all lists but only EDIT their department (no redirects)",
      "MLR Workflow: Reviewers AND approvers selected at submission time (combined selection)",
      "Auto-transition: After all reviewers complete, list auto-moves to REQUEST FOR APPROVAL",
      "Removed: PENDING ROUTING FOR APPROVAL status eliminated from workflow",
      "Creator Access: Only list creator + admin can edit/route DRAFT lists",
      "UUID Tracking: Per-role UUID tracking for multi-role users (currentRoleUuid)",
      "Email Notifications: Sent for REQUEST FOR REVIEW, REQUEST FOR APPROVAL, and APPROVED"
    ],
    keyComponents: [
      "middleware.js - Route level restrictions",
      "OverviewTable.js - Core permissions (2034 lines) with hasDepartmentAccess() and isCreatorOrAdmin()",
      "Details.js - View-only access (removed redirect logic)",
      "CreateNewListModal.js - List creation restrictions",
      "CreateListModal.js - Department selection restrictions (getDepartmentOptions) + UUID fix",
      "EditBrandIndication.js - Field editing controls & department restrictions",
      "ListApprovedModal.js - Combined reviewer/approver selection at submission",
      "SideNavBar.js - Navigation restrictions",
      "Lambda app.py - Email notifications for status changes",
      "Lambda app_config.json - Email templates for status notifications"
    ],
    statusWorkflow: {
      mlr_lists: "DRAFT → REQUEST FOR REVIEW (select both reviewers AND approvers) → (all reviewers complete) → REQUEST FOR APPROVAL (auto-transition) → (approver) → APPROVED",
      salesforce_lists: "DRAFT → REQUEST FOR APPROVAL → (incentivized approver) → APPROVED",
      old_mlr_workflow: "DRAFT → REQUEST FOR REVIEW → PENDING ROUTING FOR APPROVAL → REQUEST FOR APPROVAL → APPROVED [DEPRECATED]",
      new_mlr_workflow: "DRAFT → REQUEST FOR REVIEW (combined selection) → REQUEST FOR APPROVAL (auto) → APPROVED [CURRENT]"
    },
    departmentRestrictions: {
      hcp_marketer: "Can CREATE & EDIT: Commercial (RETAIL) lists only. Can VIEW: All lists (Medical, Commercial, Salesforce)",
      omnichannel_advisor: "Can CREATE & EDIT: Medical (MEDICAL) lists only. Can VIEW: All lists (Medical, Commercial, Salesforce)",
      admin_superadmin: "Can CREATE & EDIT: All departments (Medical, Commercial, Salesforce)",
      mlr_reviewer_approver: "Can REVIEW/APPROVE: All departments. Cannot create or edit.",
      view_only_behavior: "Edit buttons (Edit Title, Edit Metadata, Add Specialty, Delete, Route) hidden for wrong department"
    },
    creatorOnlyAccess: {
      draft_lists: "Only list creator or Admin/Super Admin can edit title, metadata, add/remove specialty codes, or route DRAFT lists",
      implementation: "isCreatorOrAdmin() function checks if current user's role UUID matches created_by UUID",
      uuid_tracking: "Per-role UUID (currentRoleUuid) stored in created_by field for multi-role users",
      fallback: "Email-based matching for backwards compatibility with old lists"
    }
  }
};
